import { useCallback, useEffect, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { Tab, Tabs, styledTabClasses, styledTabsProps } from "components/Tabs"
import { css, cx } from "@emotion/css"
import { deleteTeam, fetchTeam, fetchTeams } from "api/team"
// import Members from "./Members"
import { queryCache, useMutation, useQuery } from "react-query"

import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import CreateTeam from "./CreateTeams"
import DeleteIcon from "@material-ui/icons/Cancel"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import Members from "./Members"
import { Surface } from "components/Surface"
import { TeamMember } from "models/common"
import { ReactComponent as TeamsIcon } from "assets/icon-team.svg"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useToggle } from "hooks/useToggle"
import { SelectAgency } from "../components/SelectAgency"
import { useAgencyId } from "hooks/useAgencyId"

const styles = {
  main: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
  noDataMain: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  `,
  tab: css`
    height: 100%;
  `,
  tabLabel: css`
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 100%;
  `,
  label: css`
    min-width: 80px;
  `,
  wrapper: css`
    margin-top: var(--spacing-xxl-2);
  `,
  noDataWrapper: css`
    margin-top: 0px;
    padding-top: var(--spacing-m);
  `,
  inputRow: css`
    width: 125px;
    height: 32px;
    padding: 4px 7.3px 4px 31px;
  `,
  icon: css`
    object-fit: contain;
    color: var(--disabled-grey);
  `,
  emptyStateDiv: css`
    padding-left: var(--spacing-xxl-2);
  `,
  emptyStateWidth: css`
    width: 100%;
  `,
  surface: css`
    padding: var(--spacing-xl) var(--spacing-m) var(--spacing-m);
  `,
}

function AgencyTeams() {
  const { t } = useI18n()
  const { open, onOpen, onClose } = useToggle()
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)
  const [tab, setTab] = useState<string>()
  const { hasAuthority } = useAuthenticated()
  const { agencyId } = useAgencyId()

  const {
    data: teams,
    isLoading,
    updatedAt,
  } = useQuery([`agency-teams-${agencyId}`], fetchTeams)

  const { data: team, isLoading: isTeamLoading } = useQuery(
    [`agency-team-${agencyId}`, tab],
    fetchTeam,
    {
      enabled: !!tab,
    },
  )
  const noData = !teams?.length

  const teamMembers = team?.users?.map((tu) => ({
    name: [tu.firstName, tu.lastName].join(" "),
    ...tu,
  })) as Array<TeamMember & { name: string }>

  const onTeamSelect = useCallback((teamId?: string) => {
    !!teamId && setTab(teamId)
  }, [])

  const [delTeam] = useMutation(deleteTeam, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(`agency-teams-${agencyId}`)
    },
    onError: () => setModalInfo(undefined),
  })
  useEffect(() => {
    !!teams?.length && setTab(teams[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  const onDelete = useCallback(
    (teamId: string) => {
      setModalInfo({
        title: t("teams.list.delete.title"),
        type: "primary_destructive",
        description: t("teams.list.delete.description"),
        submitLabel: t("teams.list.delete.action"),
        onSubmit: async () => await delTeam(teamId),
      })
    },
    [delTeam, t],
  )

  const isModalOpen = Boolean(modalInfo)

  return (
    <Container>
      <Breadcrumbs section={t("nav.admin")} current={t("nav.agency.teams")} />
      <Header label={t("nav.agency.teams")}>
        <SelectAgency />
        {hasAuthority(["AUTH_CREATE_TEAM"]) && (
          <Button variant="contained" color="primary" onClick={onOpen}>
            {t("teams.create.button")}
          </Button>
        )}
        <CreateTeam open={open} onClose={onClose} />
      </Header>
      {isLoading ? (
        <Loading />
      ) : (
        <Surface mb={2} flexDirection="row" className={styles.surface}>
          <div className={cx(styles.main, { [styles.noDataMain]: noData })}>
            {!!tab ? (
              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                value={tab}
                onTabChange={onTeamSelect}
                {...styledTabsProps}
              >
                {teams?.map((team, idx) => (
                  <Tab
                    key={idx}
                    classes={styledTabClasses}
                    className={styles.tab}
                    value={team.id}
                    label={
                      <span className={styles.tabLabel}>
                        <span className={styles.label}>{team.name}</span>
                        {hasAuthority(["AUTH_DELETE_TEAM"]) && (
                          <DeleteIcon
                            onClick={(e) => onDelete(team.id)}
                            className={styles.icon}
                          />
                        )}
                      </span>
                    }
                    selected={tab === team.id}
                  />
                ))}
              </Tabs>
            ) : null}
            {!!tab ? (
              <div
                className={cx(styles.wrapper, {
                  [styles.noDataWrapper]: noData,
                })}
              >
                <Members
                  isLoading={isTeamLoading}
                  teamId={tab}
                  data={teamMembers}
                />
              </div>
            ) : null}
            {!team && !tab ? (
              <EmptyState
                title={t("agencies.team.emptyState.title")}
                className={styles.emptyStateWidth}
                icon={<TeamsIcon />}
              />
            ) : null}
          </div>
        </Surface>
      )}
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </Container>
  )
}

export default AgencyTeams
